import { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import {
  listarPendencias,
  criarPendencia,
  resolverPendencia,
  concluirPendencia,
  reabrirPendencia,
  type Pendencia,
  type PendenciaStatus,
} from '../api/pendencias';
import { useAuthStore } from '../store/authStore';

const CARGOS_ENGENHARIA = ['ENGENHARIA', 'GERENTE ENGENHARIA', 'ADMINISTRADOR'];

function relTime(iso: string) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h atrás`;
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

type Filtro = 'todas' | PendenciaStatus;
const FILTROS: { key: Filtro; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'aberta', label: 'Abertas' },
  { key: 'resolvida', label: 'Resolvidas' },
  { key: 'concluida', label: 'Concluídas' },
];

interface Props {
  clienteId: number;
  /**
   * Lista externa de pendências (vinda do hook de socket).
   * Se passada, o componente usa essa fonte (atualização em tempo real).
   * Se não, ele carrega/recarrega via REST.
   */
  pendencias?: Pendencia[];
  onChange?: (lista: Pendencia[]) => void;
  /** Chamado após criar/resolver/concluir/reabrir — fallback caso socket esteja off. */
  onCreated?: () => Promise<void> | void;
  /** Estado do socket de tempo real (se hook estiver controlando). */
  connected?: boolean;
}

export default function ChatPendencias({ clienteId, pendencias: externalList, onChange, onCreated, connected }: Props) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // No app de Engenharia o usuário conclui/devolve. Vendedor (raro aqui) só resolve.
  // O backend é a fonte de verdade; isto só controla quais botões aparecem.
  const isEngenharia = CARGOS_ENGENHARIA.includes((user?.cargo || '').toUpperCase());

  const [internalList, setInternalList] = useState<Pendencia[]>([]);
  const lista = externalList ?? internalList;

  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const listRef = useRef<HTMLDivElement>(null);

  // Carga inicial — só roda se a lista não vem de fora (modo standalone).
  useEffect(() => {
    if (externalList || !isAuthenticated) return;
    setLoading(true);
    listarPendencias(clienteId)
      .then((d) => {
        setInternalList(d);
        onChange?.(d);
      })
      .catch(() => { /* já trata no client */ })
      .finally(() => setLoading(false));
  }, [clienteId, externalList, isAuthenticated, onChange]);

  // Scroll pro final quando muda
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [lista.length]);

  const refresh = async () => {
    if (externalList) {
      // Lista controlada pelo hook (socket). Mesmo assim, se onCreated foi passado,
      // ele é nosso fallback REST caso o socket não tenha entregue o evento.
      if (onCreated) await onCreated();
      return;
    }
    const d = await listarPendencias(clienteId);
    setInternalList(d);
    onChange?.(d);
  };

  const enviar = async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await criarPendencia(clienteId, text);
      setDraft('');
      await refresh();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg || 'Erro ao criar pendência');
    } finally {
      setSubmitting(false);
    }
  };

  const acao = async (
    id: number,
    fn: (id: number) => Promise<Pendencia>,
    erro: string,
  ) => {
    setActingId(id);
    try {
      await fn(id);
      await refresh();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg || erro);
    } finally {
      setActingId(null);
    }
  };

  const counts = useMemo(() => ({
    aberta: lista.filter((p) => p.status === 'aberta').length,
    resolvida: lista.filter((p) => p.status === 'resolvida').length,
    concluida: lista.filter((p) => p.status === 'concluida').length,
  }), [lista]);

  const filtrada = useMemo(
    () => (filtro === 'todas' ? lista : lista.filter((p) => p.status === filtro)),
    [lista, filtro],
  );

  if (!isAuthenticated) {
    return (
      <div style={card}>
        <p style={{ ...subtitle, marginBottom: 8 }}>Pendências</p>
        <p style={{ fontSize: 13, color: '#666' }}>
          Faça <a href={`/login?next=${encodeURIComponent(window.location.pathname)}`} style={{ color: '#111', fontWeight: 600 }}>login</a> pra ver e criar pendências.
        </p>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <p style={subtitle}>
          Pendências
          {counts.aberta > 0 && <span style={badgeAberta}>{counts.aberta} aberta{counts.aberta > 1 ? 's' : ''}</span>}
          {counts.resolvida > 0 && <span style={badgeResolvida}>{counts.resolvida} resolvida{counts.resolvida > 1 ? 's' : ''}</span>}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#aaa' }}>
          {connected !== undefined && (
            <span title={connected ? 'Conectado em tempo real' : 'Sem conexão de tempo real'} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: connected ? '#16a34a' : '#dc2626', display: 'inline-block',
            }} />
          )}
          {user?.nome}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {FILTROS.map((f) => {
          const ativo = filtro === f.key;
          const n = f.key === 'todas' ? lista.length : counts[f.key];
          return (
            <button
              key={f.key}
              onClick={() => setFiltro(f.key)}
              style={{ ...chip, ...(ativo ? chipAtivo : {}) }}
            >
              {f.label}{n > 0 ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>

      <div ref={listRef} style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, paddingRight: 4 }}>
        {loading && <p style={emptyText}>Carregando…</p>}
        {!loading && filtrada.length === 0 && (
          <p style={emptyText}>{filtro === 'todas' ? 'Sem pendências.' : `Nenhuma pendência ${FILTROS.find((f) => f.key === filtro)?.label.toLowerCase()}.`}</p>
        )}
        {filtrada.map((p) => {
          const isMine = p.criadaPor.email === user?.email;
          const isResolv = p.status === 'resolvida';
          const isConcl = p.status === 'concluida';
          const bg = isConcl ? '#f0fdf4' : isResolv ? '#eff6ff' : (isMine ? '#fafafa' : '#fff');
          const borda = isConcl ? '#bbf7d0' : isResolv ? '#bfdbfe' : '#eee';
          return (
            <div key={p.id} style={{ ...bubble, background: bg, borderColor: borda, opacity: isConcl ? 0.9 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#111' }}>
                  {p.criadaPor.nome}
                  {p.criadaPor.cargo && <span style={{ color: '#999', fontWeight: 500, marginLeft: 6 }}>· {p.criadaPor.cargo}</span>}
                </span>
                <span style={{ fontSize: 10, color: '#999' }}>{relTime(p.createdAt)}</span>
              </div>
              <div style={{ fontSize: 13, color: isConcl ? '#444' : '#111', textDecoration: isConcl ? 'line-through' : 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {p.descricao}
              </div>
              {isResolv && p.resolvidaPor && (
                <div style={{ fontSize: 11, color: '#1d4ed8', marginTop: 6 }}>
                  ✦ Resolvida por {p.resolvidaPor.nome} · {p.resolvidaEm ? relTime(p.resolvidaEm) : ''} — aguardando conclusão
                </div>
              )}
              {isConcl && p.concluidaPor && (
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 6 }}>
                  ✓ Concluída por {p.concluidaPor.nome} · {p.concluidaEm ? relTime(p.concluidaEm) : ''}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {/* Aberta: vendedor resolve; engenharia pode concluir direto. */}
                {p.status === 'aberta' && !isEngenharia && (
                  <button onClick={() => acao(p.id, resolverPendencia, 'Erro ao marcar resolvida')} disabled={actingId === p.id} style={btnResolver}>
                    {actingId === p.id ? '…' : 'Marcar resolvida'}
                  </button>
                )}
                {p.status === 'aberta' && isEngenharia && (
                  <button onClick={() => acao(p.id, concluirPendencia, 'Erro ao concluir')} disabled={actingId === p.id} style={btnConcluir}>
                    {actingId === p.id ? '…' : 'Concluir'}
                  </button>
                )}
                {/* Resolvida: só engenharia conclui ou devolve. */}
                {isResolv && isEngenharia && (
                  <>
                    <button onClick={() => acao(p.id, concluirPendencia, 'Erro ao concluir')} disabled={actingId === p.id} style={btnConcluir}>
                      {actingId === p.id ? '…' : 'Concluir'}
                    </button>
                    <button onClick={() => acao(p.id, reabrirPendencia, 'Erro ao devolver')} disabled={actingId === p.id} style={btnDevolver}>
                      {actingId === p.id ? '…' : 'Devolver'}
                    </button>
                  </>
                )}
                {/* Concluída: engenharia pode reabrir. */}
                {isConcl && isEngenharia && (
                  <button onClick={() => acao(p.id, reabrirPendencia, 'Erro ao reabrir')} disabled={actingId === p.id} style={btnDevolver}>
                    {actingId === p.id ? '…' : 'Reabrir'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); enviar(); } }}
          placeholder="Nova pendência… (Ctrl+Enter)"
          rows={2}
          style={textarea}
        />
        <button onClick={enviar} disabled={submitting || !draft.trim()} style={btnEnviar}>
          {submitting ? '…' : 'Enviar'}
        </button>
      </div>
    </div>
  );
}

const card: React.CSSProperties = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' };
const subtitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: '#111', margin: 0 };
const badgeAberta: React.CSSProperties = { marginLeft: 8, background: '#fde68a', color: '#7c2d12', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 };
const badgeResolvida: React.CSSProperties = { marginLeft: 6, background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 };
const chip: React.CSSProperties = { fontSize: 11, padding: '3px 10px', borderRadius: 999, border: '1px solid #e5e5e5', background: '#fff', color: '#555', cursor: 'pointer', fontWeight: 600 };
const chipAtivo: React.CSSProperties = { background: '#111', color: '#fff', borderColor: '#111' };
const emptyText: React.CSSProperties = { fontSize: 12, color: '#aaa', textAlign: 'center', padding: '16px 0', margin: 0 };
const bubble: React.CSSProperties = { border: '1px solid #eee', borderRadius: 10, padding: '8px 10px', background: '#fff' };
const textarea: React.CSSProperties = { flex: 1, resize: 'none', border: '1px solid #e5e5e5', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
const btnEnviar: React.CSSProperties = { padding: '0 16px', background: '#111', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnConcluir: React.CSSProperties = { fontSize: 11, padding: '4px 10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const btnResolver: React.CSSProperties = { fontSize: 11, padding: '4px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const btnDevolver: React.CSSProperties = { fontSize: 11, padding: '4px 10px', background: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' };
